import React, { Component } from 'react'
import { withApollo } from 'react-apollo'
import gql from 'graphql-tag'
import { store } from '../../stores/reduxStore'
import { Card, CardHeader, CardBody, Row, Col, Table, Input, FormGroup, Label } from 'reactstrap'
import { Bar, HorizontalBar } from 'react-chartjs-2'
import { RiseLoader } from 'react-spinners'

const PROJECTS_QUERY = gql`
  query InspectionBacklogProjects {
    projects {
      list { id name project_companies }
    }
  }
`

const PERSON_QUERY = gql`
  query InspectionBacklogPerson($person_id: ID!) {
    person(id: $person_id) { company_ids }
  }
`

const DATA_QUERY = gql`
  query StatsInspectionBacklog($project_id: ID!) {
    stats_inspection_backlog(project_id: $project_id) {
      project_id
      project_name
      backlog_by_type { type_id type_name total_welds welded_count done backlog }
      avg_wait_days { type_name avg_days }
      sections { section_id section_name total_welds welded_count fully_inspected }
    }
  }
`

class InspectionBacklog extends Component {
  constructor(props) {
    super(props)
    this.state = {
      projects: [], userCompanyIds: [], selectedProjectId: null,
      data: null, loading: false, projectsLoading: true, error: null
    }
  }

  componentDidMount() {
    const personId = store.getState().user.data.person_id
    const params = new URLSearchParams(this.props.location && this.props.location.search)
    const urlProjectId = params.get('project_id')
    Promise.all([
      this.props.client.query({ query: PROJECTS_QUERY, fetchPolicy: 'network-only' }),
      this.props.client.query({ query: PERSON_QUERY, variables: { person_id: personId }, fetchPolicy: 'network-only' })
    ]).then(([projectsRes, personRes]) => {
      this.setState({
        projects: projectsRes.data.projects.list,
        userCompanyIds: (personRes.data.person.company_ids || []).map(String),
        projectsLoading: false
      }, () => { if (urlProjectId) this.handleProjectChange(urlProjectId) })
    }).catch(e => this.setState({ error: e.message, projectsLoading: false }))
  }

  handleProjectChange(projectId) {
    if (!projectId) {
      this.setState({ selectedProjectId: null, data: null })
      this.props.history && this.props.history.replace('/reports/inspection-backlog')
      return
    }
    this.setState({ selectedProjectId: projectId, data: null, loading: true, error: null })
    this.props.history && this.props.history.replace(`/reports/inspection-backlog?project_id=${projectId}`)
    this.props.client.query({ query: DATA_QUERY, fetchPolicy: 'network-only', variables: { project_id: projectId } })
      .then(({ data }) => this.setState({ data: data.stats_inspection_backlog[0] || null, loading: false }))
      .catch(e => this.setState({ error: e.message, loading: false }))
  }

  render() {
    const { projects, userCompanyIds, selectedProjectId, data, loading, projectsLoading, error } = this.state

    const filteredProjects = projects.filter(p => {
      if (!userCompanyIds.length) return true
      try {
        const pc = typeof p.project_companies === 'string' ? JSON.parse(p.project_companies) : (p.project_companies || [])
        return pc.some(c => userCompanyIds.includes(String(c.id)))
      } catch (e) { return false }
    })

    return (
      <div className="animated fadeIn">
        <Card>
          <CardHeader><strong><i className="fa fa-hourglass-half mr-2" />Inspection Backlog</strong></CardHeader>
          <CardBody style={{ position: 'relative', minHeight: '200px' }}>
            {(loading || projectsLoading) && <div className="spinner-item" id="loader-bg"><RiseLoader color="#20a8d8" loading={true} /></div>}
            {error && <div className="text-danger">Error: {error}</div>}

            <FormGroup row className="mb-3">
              <Label sm={2}>Project</Label>
              <Col sm={4}>
                <Input type="select" value={selectedProjectId || ''}
                  onChange={e => this.handleProjectChange(e.target.value || null)}>
                  <option value="">— select project —</option>
                  {filteredProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Input>
              </Col>
            </FormGroup>

            {!selectedProjectId && !loading && (
              <div className="text-center text-muted py-5">Select a project to view inspection backlog</div>
            )}

            {data && !loading && (() => {
              const selected = data

              const backlogChart = {
                labels: selected.backlog_by_type.map(b => b.type_name),
                datasets: [
                  { label: 'Inspected', data: selected.backlog_by_type.map(b => b.done), backgroundColor: 'rgba(75,192,75,0.7)' },
                  { label: 'Ready to inspect', data: selected.backlog_by_type.map(b => b.welded_count - b.done), backgroundColor: 'rgba(255,159,64,0.7)' },
                  { label: 'Not welded', data: selected.backlog_by_type.map(b => b.total_welds - b.welded_count), backgroundColor: 'rgba(54,162,235,0.4)' }
                ]
              }

              const sectionChart = {
                labels: selected.sections.map(s => s.section_name),
                datasets: [
                  { label: 'Fully inspected', data: selected.sections.map(s => s.fully_inspected), backgroundColor: 'rgba(75,192,75,0.7)' },
                  { label: 'Ready to inspect', data: selected.sections.map(s => s.welded_count - s.fully_inspected), backgroundColor: 'rgba(255,159,64,0.7)' },
                  { label: 'Not welded', data: selected.sections.map(s => s.total_welds - s.welded_count), backgroundColor: 'rgba(54,162,235,0.4)' }
                ]
              }

              return (
                <div>
                  <Row>
                    <Col md="6">
                      <h6>Backlog by inspection type</h6>
                      <Bar data={backlogChart} options={{
                        maintainAspectRatio: true,
                        scales: { xAxes: [{ stacked: true }], yAxes: [{ stacked: true, ticks: { beginAtZero: true } }] }
                      }} />
                    </Col>
                    <Col md="6">
                      <h6>Avg wait time (welding → inspection) [days]</h6>
                      <Table bordered size="sm">
                        <thead><tr><th>Inspection type</th><th>Avg wait [days]</th></tr></thead>
                        <tbody>
                          {selected.avg_wait_days.map((w, i) => (
                            <tr key={i}>
                              <td>{w.type_name}</td>
                              <td style={{ color: w.avg_days > 14 ? 'red' : w.avg_days > 7 ? 'orange' : 'inherit' }}>
                                <strong>{w.avg_days}</strong>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                  </Row>

                  {selected.sections.length > 0 && (() => {
                    const sf = this.state.sectionFilter || ''
                    const filteredSections = sf
                      ? selected.sections.filter(s => s.section_name.toLowerCase().includes(sf.toLowerCase()))
                      : selected.sections
                    const filteredSectionChart = {
                      labels: filteredSections.map(s => s.section_name),
                      datasets: [
                        { label: 'Fully inspected', data: filteredSections.map(s => s.fully_inspected), backgroundColor: 'rgba(75,192,75,0.7)' },
                        { label: 'Ready to inspect', data: filteredSections.map(s => s.welded_count - s.fully_inspected), backgroundColor: 'rgba(255,159,64,0.7)' },
                        { label: 'Not welded', data: filteredSections.map(s => s.total_welds - s.welded_count), backgroundColor: 'rgba(54,162,235,0.4)' }
                      ]
                    }
                    return (
                      <Row className="mt-4">
                        <Col md="12">
                          <h6>Backlog by section</h6>
                          <Row className="mb-2">
                            <Col sm={4}>
                              <Input type="text" placeholder="Filter sections..." value={sf}
                                onChange={e => this.setState({ sectionFilter: e.target.value })} />
                            </Col>
                          </Row>
                          <div style={{ height: `${Math.min(Math.max(400, filteredSections.length * 56), 1200)}px` }}>
                            <HorizontalBar data={filteredSectionChart} options={{
                              maintainAspectRatio: false,
                              scales: { xAxes: [{ stacked: true, ticks: { beginAtZero: true } }], yAxes: [{ stacked: true }] }
                            }} />
                          </div>
                        </Col>
                      </Row>
                    )
                  })()}

                  <div className="mt-3 p-3 border rounded bg-light" style={{ fontSize: '12px' }}>
                    <strong>How it's calculated:</strong>
                    <ul className="mb-0 mt-1 pl-3">
                      <li><strong>Backlog by inspection type</strong> — all non-removed welds that require a given inspection type (per <em>inspection_categories</em>); stacked: <span style={{color:'rgba(75,192,75,0.9)'}}>■</span> Inspected = has a passing log of that type, <span style={{color:'rgba(255,159,64,0.9)'}}>■</span> Ready to inspect = welding 100% but no passing log yet, <span style={{color:'rgba(54,162,235,0.7)'}}>■</span> Not welded = welding_progress &lt; 100%</li>
                      <li><strong>Avg wait days</strong> — average days between last welding log and first inspection log per weld, grouped by inspection type; <span style={{color:'orange'}}>orange</span> &gt;7 days, <span style={{color:'red'}}>red</span> &gt;14 days</li>
                      <li><strong>Backlog by section</strong> — per section: <span style={{color:'rgba(75,192,75,0.9)'}}>■</span> Fully inspected = weld has all required inspection types passed (per <em>inspection_categories</em>), <span style={{color:'rgba(255,159,64,0.9)'}}>■</span> Ready to inspect = welding 100% but not fully inspected, <span style={{color:'rgba(54,162,235,0.7)'}}>■</span> Not welded = welding_progress &lt; 100%</li>
                    </ul>
                  </div>
                </div>
              )
            })()}
          </CardBody>
        </Card>
      </div>
    )
  }
}

export default withApollo(InspectionBacklog)



// WEBPACK FOOTER //
// ./src/components/Report/InspectionBacklog.js